import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function GrandOpening() {
  const { t } = useTranslation('common');
  const [fireworks, setFireworks] = useState<Array<{ id: number; x: number; y: number; color: string }>>([]);

  useEffect(() => {
    const interval = setInterval(() => {
      const newFirework = {
        id: Date.now() + Math.random(),
        x: Math.random() * 100,
        y: Math.random() * 60 + 20,
        color: ['#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500', '#FF1493'][Math.floor(Math.random() * 8)]
      };
      setFireworks(prev => [...prev, newFirework]);
      
      setTimeout(() => {
        setFireworks(prev => prev.filter(fw => fw.id !== newFirework.id));
      }, 2000);
    }, 400);

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative py-20 overflow-hidden bg-gradient-to-br from-red-600 via-orange-500 to-yellow-500">
      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{ 
          backgroundImage: 'radial-gradient(circle, white 2px, transparent 2px)', 
          backgroundSize: '50px 50px',
          animation: 'moveBackground 20s linear infinite'
        }}></div>
      </div>

      {/* Fireworks */}
      {fireworks.map((firework) => (
        <div
          key={firework.id}
          className="absolute pointer-events-none"
          style={{
            left: `${firework.x}%`,
            top: `${firework.y}%`,
          }}
        >
          {/* Center burst */}
          <div 
            className="absolute w-4 h-4 rounded-full animate-ping"
            style={{ backgroundColor: firework.color }}
          ></div>
          
          {/* Particles */}
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full"
              style={{
                backgroundColor: firework.color,
                animation: `fireworkParticle 2s ease-out forwards`,
                transform: `rotate(${i * 30}deg)`,
                transformOrigin: 'center',
              }}
            ></div>
          ))}
        </div>
      ))}

      {/* Floating Confetti */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-3 h-3 opacity-80"
            style={{
              left: `${Math.random() * 100}%`,
              top: '-10%',
              backgroundColor: ['#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#FFA500'][Math.floor(Math.random() * 6)],
              animation: `fall ${5 + Math.random() * 5}s linear infinite`,
              animationDelay: `${Math.random() * 5}s`,
              transform: `rotate(${Math.random() * 360}deg)`,
            }}
          ></div>
        ))}
      </div>

      {/* Pulsing Circles */}
      <div className="absolute top-10 left-10 w-64 h-64 bg-yellow-400/30 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-10 right-10 w-64 h-64 bg-red-400/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Main Content */}
        <div className="text-center">
          {/* Badge */}
          <div className="inline-block mb-6 animate-bounce">
            <div className="bg-white text-red-600 px-6 py-3 rounded-full text-lg font-black shadow-2xl transform hover:scale-110 transition-all">
              <i className="ri-gift-line text-2xl mr-2"></i>
              {t('grand_opening_badge')}
            </div>
          </div>

          {/* Main Title with Animation */}
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight animate-pulse">
            <span className="inline-block transform hover:scale-110 transition-transform">{t('grand_opening_subtitle')}</span>
            <br />
            <span className="bg-white text-red-600 px-6 py-3 rounded-3xl inline-block mt-3 shadow-2xl transform hover:rotate-2 transition-all">
              {t('grand_opening_subtitle2')}
            </span>
          </h2>

          {/* Subtitle */}
          <p className="text-xl sm:text-2xl text-white font-bold mb-8 drop-shadow-2xl">
            {t('grand_opening_title')}
          </p>

          {/* Important Notice */}
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl mb-8 transform hover:scale-105 transition-all max-w-3xl mx-auto">
            <div className="flex items-center justify-center gap-4 mb-4">
              <i className="ri-alarm-warning-line text-5xl text-red-600 animate-pulse"></i>
              <h3 className="text-3xl font-black text-gray-900">{t('grand_opening_limited')}</h3>
              <i className="ri-alarm-warning-line text-5xl text-red-600 animate-pulse"></i>
            </div>
            <p className="text-xl font-bold text-gray-800 mb-3">
              {t('grand_opening_exclusive')}
            </p>
            <p className="text-lg text-gray-700 font-semibold">
              {t('grand_opening_description')}
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => {
                const element = document.getElementById('contacto');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="group bg-white text-red-600 px-10 py-4 rounded-full text-xl font-black hover:bg-gray-100 transition-all shadow-2xl hover:scale-110 hover:shadow-white/50 whitespace-nowrap"
            >
              <i className="ri-map-pin-line text-2xl mr-2 group-hover:animate-bounce"></i>
              {t('grand_opening_location')}
            </button>
            <a
              href="https://wa.me/13172790334"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-green-500 hover:bg-green-600 text-white px-10 py-4 rounded-full text-xl font-black transition-all shadow-2xl hover:scale-110 hover:shadow-green-500/50 flex items-center gap-2 whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-2xl group-hover:rotate-12 transition-transform"></i>
              {t('grand_opening_offers')}
            </a>
          </div>

          {/* Decorative Stars */}
          <div className="mt-8 flex items-center justify-center gap-6 text-white text-4xl">
            <span className="animate-spin inline-block">⭐</span>
            <span className="animate-pulse inline-block">✨</span>
            <span className="animate-bounce inline-block">🌟</span>
            <span className="animate-spin inline-block" style={{ animationDelay: '0.5s' }}>⭐</span>
            <span className="animate-pulse inline-block" style={{ animationDelay: '0.5s' }}>✨</span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fireworkParticle {
          0% {
            transform: translate(0, 0) scale(1);
            opacity: 1;
          }
          100% {
            transform: translate(var(--tx, 100px), var(--ty, 100px)) scale(0);
            opacity: 0;
          }
        }

        @keyframes fall {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(360deg);
            opacity: 0;
          }
        }

        @keyframes moveBackground {
          0% {
            transform: translate(0, 0);
          }
          100% {
            transform: translate(50px, 50px);
          }
        }

        div[style*="fireworkParticle"] {
          --tx: calc(cos(var(--angle, 0deg)) * 150px);
          --ty: calc(sin(var(--angle, 0deg)) * 150px);
        }
      `}</style>
    </section>
  );
}
